import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSubscriptionV2 as createSubscriptionV2Api,
  updateSubscriptionV2 as updateSubscriptionV2Api,
  fetchSubscriptionPreview as fetchSubscriptionPreviewApi,
  fetchMetricFilterOptionsById as fetchMetricFilterOptionsByIdApi,
  searchMetricFilterOptions as searchMetricFilterOptionsApi,
  testNotification as testNotificationApi,
} from "@/helpers/apis";
import { QUERY_OPTIONS } from "@/pages/subscriptions/services/queryConfig";
import {
  SubscriptionFormData,
  type NotificationTestRequest,
} from "@/pages/subscriptions/services/types";
import type { SubscriptionPreviewParams } from "../types";
import { transformToBackendPayload } from "../utils/subscriptionTransformers";
import { useCallback, useState } from "react";
import type { ComboboxOption } from "@altimateai/lego";

// ============================================================================
// Create/Update Subscription Mutations
// ============================================================================

// Zod validation is handled by api.post in apis.ts (via patchWithZodSchema)

export const useCreateSubscriptionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: SubscriptionFormData) =>
      createSubscriptionV2Api(transformToBackendPayload(formData)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptions-v2"] });
    },
  });
};

export const useUpdateSubscriptionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: SubscriptionFormData }) =>
      updateSubscriptionV2Api(id, transformToBackendPayload(formData)),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ["subscriptions-v2"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-v2", String(data?.id)] });
    },
  });
};

// ============================================================================
// Subscription Preview
// ============================================================================

export const useSubscriptionPreview = (
  params?: SubscriptionPreviewParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["subscription-preview", params?.rule_type, params?.metric_config],
    queryFn: () => fetchSubscriptionPreviewApi(params),
    enabled,
    ...QUERY_OPTIONS.preview,
  });
};

// ============================================================================
// Test Notification
// ============================================================================

export const useTestNotification = () => {
  return useMutation({
    mutationFn: (data: NotificationTestRequest) => testNotificationApi(data),
  });
};

// ============================================================================
// Metric Filter Options
// ============================================================================

export const useMetricFilterOptions = (filterName?: string, metricName?: string) => {
  return useQuery({
    queryKey: ["metric-filter-options", filterName, metricName],
    queryFn: () => fetchMetricFilterOptionsByIdApi(filterName!, metricName),
    enabled: !!filterName && !!metricName,
    ...QUERY_OPTIONS.reference,
  });
};

// ============================================================================
// Searchable Metric Filter Options (for high-cardinality filters like tags)
// Uses Combobox's onLoadMore pattern: returns accumulated options + loadMore callback
// ============================================================================

const SEARCHABLE_FILTER_PAGE_SIZE = 20;

export const useSearchableFilterOptions = (filterName?: string, metricName?: string) => {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const loadMore = useCallback(
    async (search?: string, page?: number) => {
      if (!filterName || !metricName) return;

      const searchValue = search ?? "";
      const pageNum = page ?? 1;

      const data = await searchMetricFilterOptionsApi({
        filterName,
        metricName,
        search: searchValue,
        page: pageNum,
        size: SEARCHABLE_FILTER_PAGE_SIZE,
      });

      if (!data) return;

      const newOptions = data.options.map(opt => ({
        label: opt.label,
        value: opt.value,
      }));

      if (pageNum === 1) {
        setOptions(newOptions);
      } else {
        setOptions(prev => {
          const existingValues = new Set(prev.map(o => o.value));
          const uniqueNew = newOptions.filter(o => !existingValues.has(o.value));
          return [...prev, ...uniqueNew];
        });
      }

      const totalPages = Math.ceil(data.total / (data.size ?? SEARCHABLE_FILTER_PAGE_SIZE));
      setHasMore(pageNum < totalPages);
    },
    [filterName, metricName]
  );

  return { options, hasMore, loadMore };
};
